import { storeWrapper } from '../storeWrapper'
import { ospreyAPI } from '@repo/api/ospreyAPI'
import { throttle, debounce } from '@repo/utils/debounce'
import { useNewMatterStore } from '../useNewMatterStore/useNewMatterStore'

const initialState = {
  allGroups: [],
  allUsers: [],
  branch: [],
  branches: [],
  claimTypes: [],
  overtimeClaimTypes: [],
  clientAutonumbering: null,
  contacts: [],
  contactsById: {},
  contactsBySearchTerms: [],
  department: [],
  departments: [],
  expertAreas: [],
  expertAreasById: {},
  expertTypes: [],
  expertTypesById: {},
  franchiseCategories: [],
  groupCodes: [],
  legalAidVersions: [],
  marketingMailingCategories: [],
  marketingIntroductionSources: [],
  matterHistoryTypes: [],
  matterRestrictedAccess: null,
  organisations: [],
  organisationsById: {},
  organisationsBySearchTerms: [],
  phoneType: [],
  phoneTypes: [],
  refreshContactsTrigger: 0,
  refreshOrganisationsTrigger: 0,
  remunerationType: [],
  remunerationTypes: [],
  retentionPolicies: [],
  reviewTypes: [],
  userCanOverrideMatterNo: null,
  userCanOverrideUFN: null,
  workflowType: [],
  workflowTypes: [],
  enableBankFeeds: false,
  enableExpenses: false,
  enableOvertime: false,
  enableBundledocs: false,
  emailIdentification: {
    htmlEmailIdentification: '',
    plainTextEmailIdentification: '',
    subjectEmailIdentification: ''
  }
}

export const useLookupsStore = storeWrapper('useLookupsStore', (set, get) => ({
  ...initialState,
  updateState: ({ key, value }) => {
    set({ [key]: value })
  },
  fetchRemunerationTypes: ({ crmSchemeId, privateOrLegalAid }) => {
    throttle({
      id: `fetchRemunerationTypes-${crmSchemeId}-${privateOrLegalAid}`,
      callback: async () => {
        set({ remunerationTypes: [] })

        const remunerationTypes = await ospreyAPI({
          service: 'lookups',
          endpoint: 'RemunerationType/GetAllByCrmSchemeIdAndPrivateOrLegalAid',
          data: { crmSchemeId, privateOrLegalAid }
        })

        set({ remunerationTypes: [...remunerationTypes] })
      }
    })
  },
  fetchRemunerationTypeById: async ({ remunerationTypeId, forceApi = false }) => {
    if (forceApi) {
      throttle({
        id: `fetchRemunerationTypeById-${remunerationTypeId}`,
        callback: async () => {
          const remunerationType = await ospreyAPI({
            service: 'lookups',
            endpoint: `RemunerationType/Get?id=${remunerationTypeId}`
          })

          set({ remunerationType })
        }
      })
    } else {
      const selectedOption = get().remunerationTypes.find(
        (remunerationType) => remunerationType.remunerationTypeId === remunerationTypeId
      )

      set({ remunerationType: selectedOption })
    }
  },
  fetchGroupCodes: () => {
    throttle({
      id: `fetchGroupCodes`,
      callback: async () => {
        set({ groupCodes: [] })

        const groupCodes = await ospreyAPI({
          service: 'lookups',
          endpoint: 'GroupCode/GetAll'
        })

        set({ groupCodes: [...groupCodes] })
      }
    })
  },
  fetchPhoneTypes: () => {
    throttle({
      id: `fetchPhoneTypes`,
      callback: async () => {
        set({ phoneTypes: [] })

        const phoneTypes = await ospreyAPI({
          service: 'lookups',
          endpoint: 'PhoneType/GetAll'
        })

        set({ phoneTypes })
      }
    })
  },
  fetchPhoneTypeById: async ({ phoneTypeId, forceApi = false }) => {
    if (forceApi) {
      throttle({
        id: `fetchPhoneTypeById-${phoneTypeId}`,
        callback: async () => {
          const phoneType = await ospreyAPI({
            service: 'lookups',
            endpoint: `PhoneType/Get?id=${phoneTypeId}`
          })

          set({ phoneType })
        }
      })
    } else {
      const selectedOption = get().phoneTypes.find((phoneType) => phoneType.phoneTypeId === phoneTypeId)

      set({ phoneType: selectedOption })
    }
  },
  fetchClientAutonumbering: async () => {
    throttle({
      id: `fetchClientAutonumbering`,
      callback: async () => {
        const clientAutonumbering = await ospreyAPI({
          service: 'lookups',
          endpoint: 'OspreyConfiguration/GetClientAutoNumbering'
        })

        set({ clientAutonumbering })
      }
    })
  },
  fetchUserCanOverrideMatterNo: async () => {
    throttle({
      id: `fetchUserCanOverrideMatterNo`,
      callback: async () => {
        const res = await ospreyAPI({
          service: 'lookups',
          endpoint: 'OspreyConfiguration/GetUserCanOverrideMatterAutoNumbering'
        })

        const userCanOverrideMatterNo = res === true || res === false ? res : null
        set({ userCanOverrideMatterNo })
      }
    })
  },
  fetchUserCanOverrideUFN: async () => {
    throttle({
      id: `fetchUserCanOverrideUFN`,
      callback: async () => {
        const res = await ospreyAPI({
          service: 'lookups',
          endpoint: 'OspreyConfiguration/GetUserCanOverrideMatterUniqueFileNumber'
        })

        const userCanOverrideUFN = res === true || res === false ? res : null
        set({ userCanOverrideUFN })
      }
    })
  },
  fetchReviewTypes: async () => {
    throttle({
      id: `fetchReviewTypes`,
      callback: async () => {
        const reviewTypes = await ospreyAPI({
          service: 'lookups',
          endpoint: 'ReviewType/GetAll'
        })

        set({ reviewTypes })
      }
    })
  },
  fetchWorkflowTypes: async () => {
    throttle({
      id: `fetchWorkflowTypes`,
      callback: async () => {
        let workflowTypes = await ospreyAPI({
          service: 'lookups',
          endpoint: 'WorkflowType/GetAll'
        })

        workflowTypes = workflowTypes.sort((a, b) => {
          if (a.description.toLowerCase() < b.description.toLowerCase()) return -1

          return 1
        })

        set({ workflowTypes })
      }
    })
  },
  fetchWorkflowTypeById: async ({ workflowTypeId, forceApi = false }) => {
    if (forceApi) {
      throttle({
        id: `fetchWorkflowTypeById-${workflowTypeId}`,
        callback: async () => {
          const workflowType = await ospreyAPI({
            service: 'lookups',
            endpoint: `WorkflowType/Get?id=${workflowTypeId}`
          })

          set({ workflowType })
        }
      })
    } else {
      const selectedOption = get().workflowTypes.find((workflowType) => workflowType.workflowTypeId === workflowTypeId)

      set({ workflowType: selectedOption })
    }
  },
  fetchBranches: async () => {
    throttle({
      id: `fetchBranches`,
      callback: async () => {
        const branches = await ospreyAPI({
          service: 'lookups',
          endpoint: 'Branch/GetAll'
        })

        set({ branches })
      }
    })
  },
  fetchBranchById: async ({ branchId, forceApi = false }) => {
    if (forceApi) {
      throttle({
        id: `fetchSingleBranch-${branchId}`,
        callback: async () => {
          const branch = await ospreyAPI({
            service: 'lookups',
            endpoint: `Branch/Get?id=${branchId}`
          })

          set({ branch })
        }
      })
    } else {
      const selectedOption = get().branches.find((branch) => branch.branchId === branchId)

      set({ branch: selectedOption })
    }
  },
  fetchDepartments: async () => {
    throttle({
      id: `fetchDepartments`,
      callback: async () => {
        const departments = await ospreyAPI({
          service: 'lookups',
          endpoint: 'Department/GetAll'
        })

        set({ departments })
      }
    })
  },
  fetchDepartmentById: async ({ departmentId, forceApi = false }) => {
    if (forceApi) {
      throttle({
        id: `fetchSingleDepartment-${departmentId}`,
        callback: async () => {
          const department = await ospreyAPI({
            service: 'lookups',
            endpoint: `Department/Get?id=${departmentId}`
          })

          set({ department })
        }
      })
    } else {
      const selectedOption = get().departments.find((department) => department.departmentId === departmentId)

      set({ department: selectedOption })
    }
  },
  fetchFranchiseCategories: async () => {
    throttle({
      id: `fetchFranchiseCategories`,
      callback: async () => {
        const franchiseCategories = await ospreyAPI({
          service: 'lookups',
          endpoint: 'Franchise/GetAll'
        })

        set({ franchiseCategories })
      }
    })
  },
  fetchLegalAidVersions: async () => {
    throttle({
      id: `fetchLegalAidVersions`,
      callback: async () => {
        const legalAidVersions = await ospreyAPI({
          service: 'lookups',
          endpoint: 'CrmScheme/GetAll'
        })

        set({ legalAidVersions })
      }
    })
  },
  fetchAllUsers: async () => {
    throttle({
      id: `fetchAllUsers`,
      callback: async () => {
        const allUsers = await ospreyAPI({
          service: 'security',
          endpoint: 'User/GetAllAbbreviated'
        })

        set({ allUsers })
      }
    })
  },
  fetchAllGroups: async () => {
    throttle({
      id: `fetchAllGroups`,
      callback: async () => {
        const allGroups = await ospreyAPI({
          service: 'security',
          endpoint: 'SecurityGroup/GetAll'
        })

        set({ allGroups })
      }
    })
  },
  fetchMatterRestrictedAccess: async () => {
    throttle({
      id: `fetchMatterRestrictedAccess`,
      callback: async () => {
        const res = await ospreyAPI({
          service: 'lookups',
          endpoint: 'OspreyConfiguration/GetMatterRestrictedAccess'
        })

        set({ matterRestrictedAccess: res === true })
      }
    })
  },
  fetchBankFeedConfigSwitch: async () => {
    throttle({
      id: `fetchBankFeedConfigSwitch`,
      callback: async () => {
        const res = await ospreyAPI({
          service: 'lookups',
          endpoint: 'OspreyConfiguration/GetEnableBanking'
        })

        set({ enableBankFeeds: res === true })
      }
    })
  },
  fetchEnableBundledocs: async () => {
    throttle({
      id: `fetchEnableBundledocs`,
      callback: async () => {
        const res = await ospreyAPI({
          service: 'lookups',
          endpoint: 'OspreyConfiguration/GetEnableBundledocsIntegration'
        })

        set({ enableBundledocs: res === true })
      }
    })
  },
  fetchOspreyStartupConfiguration: async () => {
    throttle({
      id: `fetchOspreyStartupConfiguration`,
      callback: async () => {
        const res = await ospreyAPI({
          service: 'lookups',
          endpoint: 'OspreyConfiguration/GetOspreyStartupConfiguration'
        })

        set({
          matterRestrictedAccess: res.matterRestrictedAccess === true,
          userCanOverrideMatterNo:
            res.userCanOverrideMatterAutoNumbering === true || res.userCanOverrideMatterAutoNumbering === false
              ? res.userCanOverrideMatterAutoNumbering
              : null,
          userCanOverrideUFN:
            res.userCanOverrideMatterUniqueFileNumber === true || res.userCanOverrideMatterUniqueFileNumber === false
              ? res.userCanOverrideMatterUniqueFileNumber
              : null,
          clientAutonumbering: res.clientAutoNumbering,
          enableBankFeeds: res.enableBanking === true,
          enableExpenses: res.enableExpenses === true,
          enableOvertime: res.enableOvertime === true
        })

        useNewMatterStore.setState({
          defaultProspectValue: res.matterProspectDefaultValue === true,
          prospect: res.matterProspectDefaultValue === true
        })
      }
    })
  },
  fetchExpertTypes: async () => {
    throttle({
      id: `fetchExpertTypes`,
      callback: async () => {
        const expertTypes = await ospreyAPI({
          service: 'lookups',
          endpoint: 'ExpertType/GetAll'
        })

        const expertTypesById = {}
        expertTypes.forEach((expertType) => {
          expertTypesById[expertType.expertTypeId] = expertType
        })

        set({ expertTypes, expertTypesById })
      }
    })
  },
  fetchExpertiseAreas: async () => {
    throttle({
      id: `fetchExpertiseAreas`,
      callback: async () => {
        const expertAreas = await ospreyAPI({
          service: 'lookups',
          endpoint: 'ExpertiseArea/GetAll'
        })

        const expertAreasById = {}
        expertAreas.forEach((expertArea) => {
          expertAreasById[expertArea.expertiseAreaId] = expertArea
        })

        set({ expertAreas, expertAreasById })
      }
    })
  },
  fetchContacts: async () => {
    throttle({
      id: `fetchContacts`,
      callback: async () => {
        const contacts = await ospreyAPI({
          service: 'lookups',
          endpoint: 'Contact/GetAll'
        })

        const contactsById = {}
        contacts.forEach((contact) => {
          contactsById[contact.contactId] = contact
        })

        set({ contacts, contactsById: contacts })
      }
    })
  },
  fetchContactById: async ({ id, forceFetch = false }) => {
    return new Promise((resolve, reject) => {
      throttle({
        id: `fetchContactById-${id}`,
        callback: async () => {
          const { contactsById } = get()

          if (contactsById[id] && !forceFetch) {
            resolve(contactsById[id])
            return
          }

          try {
            const contact = await ospreyAPI({
              service: 'lookups',
              endpoint: 'Contact/Get',
              data: { id },
              suppress404: true
            })

            set({ contactsById: { ...contactsById, [contact.contactId]: contact } })
            resolve(contact)
          } catch (error) {
            reject(error)
          }
        }
      })
    })
  },
  fetchContactsBySearchTerm: async ({
    searchTerm,
    expertTypeId,
    marketingMailingCategoryId,
    marketingIntroductionSourceId,
    hasOptedInToEmail,
    startAtIndex = 0,
    pageSize = 50
  }) => {
    return new Promise((resolve) => {
      throttle({
        id: `fetchContactsBySearchTerm`,
        callback: async () => {
          const data = {
            StartAtIndex: startAtIndex,
            PageSize: pageSize,
            OrderByColumns: [
              {
                columnName: 'surname',
                direction: 0
              }
            ],
            HasEmail: false // this means don't filter results by whether the contact has emails - it doesn't mean return items with no emails
          }

          if (searchTerm) {
            data.SearchTerm = searchTerm
          }

          if (expertTypeId) {
            data.ExpertTypeId = expertTypeId
          }

          if (marketingMailingCategoryId) {
            data.MarketingMailingCategoryId = marketingMailingCategoryId
          }

          if (marketingIntroductionSourceId) {
            data.MarketingIntroductionSourceId = marketingIntroductionSourceId
          }

          if (hasOptedInToEmail) {
            data.HasOptedInToEmail = hasOptedInToEmail
          }

          const { rows, totalRowCount } = await ospreyAPI({
            service: 'lookups',
            endpoint: 'Contact/GetAllBySearchTerm',
            data
          })

          const { contactsById } = get()
          rows.forEach((contact) => {
            contactsById[contact.contactId] = contact
          })

          set({ contactsById: { ...contactsById } })
          resolve({ rows, totalRowCount, contactsById })
        }
      })
    })
  },
  fetchClaimTypes: async () => {
    return new Promise((resolve) => {
      throttle({
        id: `fetchClaimTypes`,
        callback: async () => {
          const claimTypes = await ospreyAPI({
            service: 'accounts',
            endpoint: 'ExpenseClaimType/GetAll'
          })
          set({ claimTypes })
          resolve(claimTypes)
        }
      })
    })
  },
  fetchOvertimeClaimTypes: async () => {
    return new Promise((resolve) => {
      throttle({
        id: `fetchOvertimeClaimTypes`,
        callback: async () => {
          const overtimeClaimTypes = await ospreyAPI({
            service: 'accounts',
            endpoint: 'OvertimeClaimType/GetAll'
          })
          set({ overtimeClaimTypes })
          resolve(overtimeClaimTypes)
        }
      })
    })
  },
  fetchAllContactsBySearchTermAsCSV: async ({
    searchTerm,
    expertTypeId,
    hasEmail,
    marketingMailingCategoryId,
    marketingIntroductionSourceId,
    hasOptedInToEmail,
    orderByColumns,
    startAtIndex,
    pageSize
  }) => {
    return new Promise((resolve) => {
      throttle({
        id: `fetchAllContactsBySearchTermAsCSV`,
        callback: async () => {
          const data = {
            ExpertTypeId: expertTypeId,
            HasEmail: hasEmail,
            MarketingMailingCategoryId: marketingMailingCategoryId,
            MarketingIntroductionSourceId: marketingIntroductionSourceId,
            HasOptedInToEmail: hasOptedInToEmail,
            OrderByColumns: orderByColumns,
            StartAtIndex: startAtIndex,
            PageSize: pageSize
          }

          if (searchTerm) {
            data.SearchTerm = searchTerm
          }

          const csv = await ospreyAPI({
            service: 'lookups',
            endpoint: 'Contact/GetAllBySearchTermAsCSV',
            data: data,
            responseType: 'blob'
          })

          const blob = new Blob([csv], { type: 'text/csv' })

          const blobUrl = URL.createObjectURL(blob)
          const link = document.createElement('a')
          link.href = blobUrl
          link.download = 'contacts_export.csv'
          document.body.appendChild(link)
          link.click()
          document.body.removeChild(link)

          URL.revokeObjectURL(blobUrl)

          resolve(csv)
        }
      })
    })
  },
  fetchAllMarketingIntroductionSource: async () => {
    return new Promise((resolve) => {
      throttle({
        id: `fetchAllMarketingIntroductionSource`,
        callback: async () => {
          const marketingIntroductionSources = await ospreyAPI({
            service: 'lookups',
            endpoint: 'MarketingIntroductionSource/GetAll'
          })
          set({ marketingIntroductionSources })
          resolve(marketingIntroductionSources)
        }
      })
    })
  },
  fetchAllMarketingMailingCategories: async () => {
    return new Promise((resolve) => {
      throttle({
        id: `fetchMarketingMailingCategories`,
        callback: async () => {
          const marketingMailingCategories = await ospreyAPI({
            service: 'lookups',
            endpoint: 'MarketingMailingCategory/GetAll'
          })
          set({ marketingMailingCategories })
          resolve(marketingMailingCategories)
        }
      })
    })
  },
  fetchMarketingIntroductionSourceById: async ({ marketingIntroductionSourceId }) => {
    return new Promise((resolve) => {
      throttle({
        id: `fetchMarketingIntroductionSourceById-${marketingIntroductionSourceId}`,
        callback: async () => {
          const marketingIntroductionSource = await ospreyAPI({
            service: 'lookups',
            endpoint: 'MarketingIntroductionSource/Get',
            data: { id: marketingIntroductionSourceId }
          })
          resolve(marketingIntroductionSource)
        }
      })
    })
  },
  fetchAllOrganisationsBySearchTermAsCSV: async ({
    expertTypeId,
    hasEmail,
    hasOptedInToEmail,
    marketingIntroductionSourceId,
    marketingMailingCategoryId,
    orderByColumns,
    pageSize,
    searchTerm,
    startAtIndex
  }) => {
    return new Promise((resolve) => {
      throttle({
        id: `fetchAllOrganisationsBySearchTermAsCSV`,
        callback: async () => {
          const data = {
            ExpertTypeId: expertTypeId,
            HasEmail: hasEmail,
            HasOptedInToEmail: hasOptedInToEmail,
            MarketingIntroductionSourceId: marketingIntroductionSourceId,
            MarketingMailingCategoryId: marketingMailingCategoryId,
            OrderByColumns: orderByColumns,
            PageSize: pageSize,
            StartAtIndex: startAtIndex
          }

          if (searchTerm) {
            data.SearchTerm = searchTerm
          }

          const csv = await ospreyAPI({
            service: 'lookups',
            endpoint: 'Organisation/GetAllBySearchTermAsCSV',
            data: data,
            responseType: 'blob'
          })

          const blob = new Blob([csv], { type: 'text/csv' })

          const blobUrl = URL.createObjectURL(blob)
          const link = document.createElement('a')
          link.href = blobUrl
          link.download = 'organisations_export.csv'
          document.body.appendChild(link)
          link.click()
          document.body.removeChild(link)

          URL.revokeObjectURL(blobUrl)

          resolve(csv)
        }
      })
    })
  },
  fetchSingleMarketingMailingCategory: async ({ marketingMailingCategoryId }) => {
    return new Promise((resolve) => {
      throttle({
        id: `fetchSingleMarketingMailingCategory-${marketingMailingCategoryId}`,
        callback: async () => {
          const marketingMailingCategory = await ospreyAPI({
            service: 'lookups',
            endpoint: `MarketingMailingCategory/Get?id=${marketingMailingCategoryId}`
          })
          resolve(marketingMailingCategory)
        }
      })
    })
  },
  fetchMarketingMailingCategoryByDescription: async ({ description, excludeId }) => {
    return new Promise((resolve) => {
      throttle({
        id: `fetchMarketingMailingCategoryByDescription-${description}`,
        callback: async () => {
          const marketingMailingCategories = await ospreyAPI({
            service: 'lookups',
            endpoint: `MarketingMailingCategory/GetByDescription`,
            data: {
              description: description,
              excludeId: excludeId || null
            }
          })
          resolve(marketingMailingCategories)
        }
      })
    })
  },
  fetchMarketingIntroductionSourceByDescription: async ({ description, excludeId }) => {
    return new Promise((resolve) => {
      throttle({
        id: `fetchMarketingIntroductionSourceByDescription-${description}`,
        callback: async () => {
          const marketingIntroductionSources = await ospreyAPI({
            service: 'lookups',
            endpoint: 'MarketingIntroductionSource/GetByDescription',
            data: {
              description: description,
              excludeId: excludeId
            }
          })
          resolve(marketingIntroductionSources)
        }
      })
    })
  },
  createMarketingMailingCategory: async ({ description, marketingMailingCategoryId }) => {
    return new Promise((resolve) => {
      throttle({
        id: `createMarketingMailingCategory`,
        callback: async () => {
          const marketingMailingCategory = await ospreyAPI({
            method: 'POST',
            service: 'lookups',
            endpoint: `MarketingMailingCategory/Create`,
            data: {
              description: description,
              marketingMailingCategoryId: marketingMailingCategoryId || null
            }
          })
          resolve(marketingMailingCategory)
        }
      })
    })
  },
  createMarketingIntroductionSource: async ({ description, marketingIntroductionSourceId }) => {
    return new Promise((resolve) => {
      throttle({
        id: `createMarketingIntroductionSource`,
        callback: async () => {
          const marketingIntroductionSource = await ospreyAPI({
            service: 'lookups',
            endpoint: 'MarketingIntroductionSource/Create',
            method: 'POST',
            data: {
              description: description,
              marketingIntroductionSourceId: marketingIntroductionSourceId
            }
          })
          resolve(marketingIntroductionSource)
        }
      })
    })
  },
  updateMarketingMailingCategory: async ({ description, marketingMailingCategoryId }) => {
    return new Promise((resolve) => {
      throttle({
        id: `updateMarketingMailingCategory`,
        callback: async () => {
          const marketingMailingCategory = await ospreyAPI({
            method: 'PUT',
            service: 'lookups',
            endpoint: `MarketingMailingCategory/Update`,
            data: {
              description: description,
              marketingMailingCategoryId: marketingMailingCategoryId
            }
          })
          resolve(marketingMailingCategory)
        }
      })
    })
  },
  updateMarketingIntroductionSource: async ({ description, marketingIntroductionSourceId }) => {
    return new Promise((resolve) => {
      throttle({
        id: `updateMarketingIntroductionSource`,
        callback: async () => {
          const marketingIntroductionSource = await ospreyAPI({
            service: 'lookups',
            endpoint: 'MarketingIntroductionSource/Update',
            method: 'PUT',
            data: {
              description: description,
              marketingIntroductionSourceId: marketingIntroductionSourceId
            }
          })
          resolve(marketingIntroductionSource)
        }
      })
    })
  },
  deleteMarketingMailingCategory: async ({ marketingMailingCategoryId }) => {
    return new Promise((resolve) => {
      throttle({
        id: `deleteMarketingMailingCategory`,
        callback: async () => {
          await ospreyAPI({
            method: 'DELETE',
            service: 'lookups',
            endpoint: `MarketingMailingCategory/Delete`,
            data: {
              marketingMailingCategoryId: marketingMailingCategoryId
            },
            sendAsUrlParams: true
          })
          resolve()
        }
      })
    })
  },
  deleteMarketingIntroductionSource: async ({ marketingIntroductionSourceId }) => {
    return new Promise((resolve) => {
      throttle({
        id: `deleteMarketingIntroductionSource`,
        callback: async () => {
          await ospreyAPI({
            service: 'lookups',
            endpoint: 'MarketingIntroductionSource/Delete',
            method: 'DELETE',
            sendAsUrlParams: true,
            data: {
              id: marketingIntroductionSourceId
            }
          })
          resolve()
        }
      })
    })
  },
  fetchAllContactsBySearchTerms: async ({
    expertTypeId,
    hasEmail,
    hasOptedInToEmail,
    marketingIntroductionSourceId,
    marketingMailingCategoryId,
    orderByColumns,
    pageSize,
    searchTerm,
    startAtIndex
  }) => {
    return new Promise((resolve) => {
      throttle({
        id: `fetchAllContactsBySearchTerms`,
        callback: async () => {
          const data = {
            ExpertTypeId: expertTypeId,
            HasEmail: hasEmail,
            HasOptedInToEmail: hasOptedInToEmail,
            MarketingIntroductionSourceId: marketingIntroductionSourceId,
            MarketingMailingCategoryId: marketingMailingCategoryId,
            OrderByColumns: orderByColumns,
            PageSize: pageSize,
            SearchTerm: searchTerm,
            StartAtIndex: startAtIndex
          }

          const contactsBySearchTerm = await ospreyAPI({
            service: 'lookups',
            endpoint: 'Contact/GetAllBySearchTerm',
            data: data
          })

          set({ contactsBySearchTerms: contactsBySearchTerm })
          resolve({ contactsBySearchTerm })
        }
      })
    })
  },
  fetchAllOrganisationsBySearchTerms: async ({
    expertTypeId,
    hasEmail,
    hasOptedInToEmail,
    marketingIntroductionSourceId,
    marketingMailingCategoryId,
    orderByColumns,
    pageSize,
    searchTerm,
    startAtIndex
  }) => {
    return new Promise((resolve) => {
      throttle({
        id: `fetchAllOrganisationsBySearchTerms`,
        callback: async () => {
          const data = {
            ExpertTypeId: expertTypeId,
            HasEmail: hasEmail,
            HasOptedInToEmail: hasOptedInToEmail,
            MarketingIntroductionSourceId: marketingIntroductionSourceId,
            MarketingMailingCategoryId: marketingMailingCategoryId,
            OrderByColumns: orderByColumns,
            PageSize: pageSize,
            SearchTerm: searchTerm,
            StartAtIndex: startAtIndex
          }

          const organisationsBySearchTerm = await ospreyAPI({
            service: 'lookups',
            endpoint: 'Organisation/GetAllBySearchTerm',
            data: data
          })

          set({ organisationsBySearchTerms: organisationsBySearchTerm })
          resolve({ organisationsBySearchTerm })
        }
      })
    })
  },
  createContact: async (data) => {
    const {
      title,
      initials,
      forename,
      surname,
      phoneNo,
      mobileNo,
      faxNo,
      email,
      house,
      area,
      postalTown,
      county,
      postcode,
      websiteUrl,
      expertTypeId,
      expertiseAreaId,
      organisationId,
      notes,
      marketingMailingCategoryId,
      marketingIntroductionSourceId,
      hasOptedInToEmail,
      optedInToEmailDateTime,
      optedInToEmailSourceUrl
    } = data

    return new Promise((resolve, reject) => {
      throttle({
        id: `createContact`,
        callback: async () => {
          try {
            const contact = await ospreyAPI({
              service: 'lookups',
              endpoint: 'Contact/Create',
              method: 'POST',
              data: {
                title,
                initials,
                forename,
                surname,
                phoneNo,
                mobileNo,
                faxNo,
                email,
                house: house || '',
                area: area || '',
                postalTown: postalTown || '',
                county: county || '',
                postcode: postcode || '',
                websiteUrl,
                expertTypeId,
                expertiseAreaId,
                organisationId,
                notes,
                marketingMailingCategoryId,
                marketingIntroductionSourceId,
                hasOptedInToEmail,
                optedInToEmailDateTime,
                optedInToEmailSourceUrl
              }
            })

            const { contactsById } = get()
            set({ contactsById: { ...contactsById, [contact.contactId]: contact } })
            resolve(contact)
          } catch (err) {
            reject(err)
          }
        }
      })
    })
  },
  updateContact: async (data) => {
    const {
      contactId,
      title,
      initials,
      forename,
      surname,
      phoneNo,
      mobileNo,
      faxNo,
      email,
      house,
      area,
      postalTown,
      county,
      postcode,
      websiteUrl,
      expertTypeId,
      expertiseAreaId,
      organisationId,
      notes,
      marketingMailingCategoryId,
      marketingIntroductionSourceId,
      hasOptedInToEmail,
      optedInToEmailDateTime,
      optedInToEmailSourceUrl
    } = data

    return new Promise((resolve, reject) => {
      throttle({
        id: `updateContact`,
        callback: async () => {
          try {
            await ospreyAPI({
              service: 'lookups',
              endpoint: 'Contact/Update',
              method: 'PUT',
              data: {
                contactId,
                title,
                initials,
                forename,
                surname,
                phoneNo,
                mobileNo,
                faxNo,
                email,
                house: house || '',
                area: area || '',
                postalTown: postalTown || '',
                county: county || '',
                postcode: postcode || '',
                websiteUrl,
                expertTypeId,
                expertiseAreaId,
                organisationId,
                notes,
                marketingMailingCategoryId,
                marketingIntroductionSourceId,
                hasOptedInToEmail,
                optedInToEmailDateTime,
                optedInToEmailSourceUrl
              }
            })

            await get().fetchContactById({ id: contactId, forceFetch: true })
            resolve()
          } catch (err) {
            reject(err)
          }
        }
      })
    })
  },
  deleteContact: async ({ id }) => {
    return new Promise((resolve) => {
      throttle({
        id: `deleteContact`,
        callback: async () => {
          await ospreyAPI({
            service: 'lookups',
            endpoint: 'Contact/Delete',
            method: 'DELETE',
            sendAsUrlParams: true,
            data: {
              id
            }
          })

          const { contactsById } = get()
          delete contactsById[id]
          set({ contactsById: { ...contactsById }, refreshContactsTrigger: get().refreshContactsTrigger + 1 })
          resolve()
        }
      })
    })
  },
  fetchOrganisations: async () => {
    throttle({
      id: `fetchOrganisations`,
      callback: async () => {
        const organisations = await ospreyAPI({
          service: 'lookups',
          endpoint: 'Organisation/GetAll'
        })

        const organisationsById = {}
        organisations.forEach((organisation) => {
          organisationsById[organisation.organisationId] = organisation
        })

        set({ organisations, organisationsById })
      }
    })
  },
  fetchOrganisationById: async ({ id, forceFetch = false }) => {
    return new Promise((resolve) => {
      throttle({
        id: `fetchOrganisation-${id}`,
        callback: async () => {
          const { organisationsById } = get()

          if (organisationsById[id] && !forceFetch) {
            resolve(organisationsById[id])
            return
          }

          const organisation = await ospreyAPI({
            service: 'lookups',
            endpoint: 'Organisation/Get',
            data: { id },
            suppressErrorToast: true
          })

          set({ organisationsById: { ...organisationsById, [organisation.organisationId]: organisation } })
          resolve(organisation)
        }
      })
    })
  },
  fetchOrganisationsBySearchTerm: async ({
    searchTerm,
    expertTypeId,
    marketingMailingCategoryId,
    marketingIntroductionSourceId,
    hasOptedInToEmail,
    startAtIndex = 0,
    pageSize = 50
  }) => {
    return new Promise((resolve) => {
      throttle({
        id: `fetchOrganisationsBySearchTerm`,
        callback: async () => {
          const data = {
            StartAtIndex: startAtIndex,
            PageSize: pageSize,
            OrderByColumns: [
              {
                columnName: 'name',
                direction: 0
              }
            ],
            HasEmail: false // this means don't filter results by whether the contact has emails - it doesn't mean return items with no emails
          }

          if (searchTerm) {
            data.SearchTerm = searchTerm
          }

          if (expertTypeId) {
            data.ExpertTypeId = expertTypeId
          }

          if (marketingMailingCategoryId) {
            data.MarketingMailingCategoryId = marketingMailingCategoryId
          }

          if (marketingIntroductionSourceId) {
            data.MarketingIntroductionSourceId = marketingIntroductionSourceId
          }

          if (hasOptedInToEmail) {
            data.HasOptedInToEmail = hasOptedInToEmail
          }

          const { rows, totalRowCount } = await ospreyAPI({
            service: 'lookups',
            endpoint: 'Organisation/GetAllBySearchTerm',
            data
          })

          const { organisationsById } = get()
          rows.forEach((organisation) => {
            organisationsById[organisation.organisationId] = organisation
          })

          set({ organisationsById: { ...organisationsById } })
          resolve({ rows, totalRowCount, organisationsById })
        }
      })
    })
  },
  fetchMatterHistoryTypes: async () => {
    return new Promise((resolve) => {
      debounce({
        id: `getAllMatterHistoryTypes`,
        delay: 300,
        callback: async () => {
          let data

          try {
            data =
              (await ospreyAPI({
                method: 'GET',
                service: 'matters',
                endpoint: '/MatterHistoryType/GetAll'
              })) || {}
          } catch (err) {
            console.log('Error:', err)
            return
          }

          set({ matterHistoryTypes: data })
          resolve(data)
        }
      })
    })
  },
  fetchRetentionPolicies: async () => {
    return new Promise((resolve) => {
      debounce({
        id: `getAllRetentionPolicies`,
        delay: 300,
        callback: async () => {
          let data

          try {
            data =
              (await ospreyAPI({
                method: 'GET',
                service: 'lookups',
                endpoint: '/RetentionPolicy/GetAll'
              })) || {}
          } catch (err) {
            console.log('Error:', err)
            return
          }

          set({ retentionPolicies: data })
          resolve(data)
        }
      })
    })
  },
  createOrganisation: async (data) => {
    const {
      organisationId,
      name,
      phoneNo,
      dxNo,
      faxNo,
      email,
      house,
      area,
      postalTown,
      county,
      postcode,
      websiteUrl,
      expertTypeId,
      notes,
      marketingMailingCategoryId,
      marketingIntroductionSourceId,
      hasOptedInToEmail,
      optedInToEmailDateTime,
      optedInToEmailSourceUrl
    } = data

    return new Promise((resolve, reject) => {
      throttle({
        id: `createOrganisation`,
        callback: async () => {
          try {
            const organisation = await ospreyAPI({
              service: 'lookups',
              endpoint: 'Organisation/Create',
              method: 'POST',
              data: {
                organisationId,
                name,
                phoneNo,
                dxNo,
                faxNo,
                email,
                house: house || '',
                area: area || '',
                postalTown: postalTown || '',
                county: county || '',
                postcode: postcode || '',
                websiteUrl,
                expertTypeId,
                notes,
                marketingMailingCategoryId,
                marketingIntroductionSourceId,
                hasOptedInToEmail,
                optedInToEmailDateTime,
                optedInToEmailSourceUrl
              }
            })

            const { organisationsById } = get()
            set({ organisationsById: { ...organisationsById, [organisation.organisationId]: organisation } })
            resolve(organisation)
          } catch (err) {
            reject(err)
          }
        }
      })
    })
  },
  updateOrganisation: async (data) => {
    const {
      organisationId,
      name,
      phoneNo,
      dxNo,
      faxNo,
      email,
      house,
      area,
      postalTown,
      county,
      postcode,
      websiteUrl,
      expertTypeId,
      notes,
      marketingMailingCategoryId,
      marketingIntroductionSourceId,
      hasOptedInToEmail,
      optedInToEmailDateTime,
      optedInToEmailSourceUrl
    } = data

    return new Promise((resolve, reject) => {
      throttle({
        id: `updateOrganisation`,
        callback: async () => {
          try {
            await ospreyAPI({
              service: 'lookups',
              endpoint: 'Organisation/Update',
              method: 'PUT',
              data: {
                organisationId,
                name,
                phoneNo,
                dxNo,
                faxNo,
                email,
                house: house || '',
                area: area || '',
                postalTown: postalTown || '',
                county: county || '',
                postcode: postcode || '',
                websiteUrl,
                expertTypeId,
                notes,
                marketingMailingCategoryId,
                marketingIntroductionSourceId,
                hasOptedInToEmail,
                optedInToEmailDateTime,
                optedInToEmailSourceUrl
              }
            })

            await get().fetchOrganisationById({ id: organisationId, forceFetch: true })
            resolve()
          } catch (err) {
            reject(err)
          }
        }
      })
    })
  },
  deleteOrganisation: async ({ id }) => {
    return new Promise((resolve, reject) => {
      throttle({
        id: `deleteOrganisation`,
        callback: async () => {
          try {
            await ospreyAPI({
              service: 'lookups',
              endpoint: 'Organisation/Delete',
              method: 'DELETE',
              sendAsUrlParams: true,
              suppressErrorToast: true,
              data: {
                id
              }
            })

            const { organisationsById } = get()
            delete organisationsById[id]
            set({
              organisationsById: { ...organisationsById },
              refreshOrganisationsTrigger: get().refreshOrganisationsTrigger + 1
            })
            resolve()
          } catch (err) {
            reject(err)
          }
        }
      })
    })
  },
  checkIfOrganisationIdExists: async ({ id }) => {
    return new Promise((resolve) => {
      throttle({
        id: `deleteOrganisation`,
        callback: async () => {
          const res = await ospreyAPI({
            service: 'lookups',
            endpoint: 'Organisation/Exists',
            method: 'GET',
            data: {
              id
            }
          })

          resolve(res === true)
        }
      })
    })
  },
  fetchEmailIdentification: async () => {
    return new Promise((resolve, reject) => {
      debounce({
        id: `fetchEmailIdentification`,
        delay: 300,
        callback: async () => {
          try {
            const emailIdentification = await ospreyAPI({
              method: 'GET',
              service: 'lookups',
              endpoint: '/OspreyConfiguration/GetEmailIdentification'
            })

            set({ emailIdentification })
            resolve(emailIdentification)
          } catch (err) {
            console.error('Error:', err)
            reject(err)
          }
        }
      })
    })
  },
  setEmailIdentification: ({ htmlEmailIdentification, plainTextEmailIdentification, subjectEmailIdentification }) => {
    return new Promise((resolve, reject) => {
      debounce({
        id: `setEmailIdentification`,
        delay: 300,
        callback: async () => {
          try {
            const emailIdentification = await ospreyAPI({
              method: 'PUT',
              service: 'lookups',
              endpoint: '/OspreyConfiguration/SetEmailIdentification',
              data: {
                htmlEmailIdentification: htmlEmailIdentification || '',
                plainTextEmailIdentification: plainTextEmailIdentification || '',
                subjectEmailIdentification: subjectEmailIdentification || ''
              }
            })

            resolve(emailIdentification)
          } catch (err) {
            console.error('Error:', err)
            reject(err)
          }
        }
      })
    })
  },
  fetchFormEvoNotificationBehaviour: async () => {
    return new Promise((resolve, reject) => {
      debounce({
        id: `fetchFormEvoNotificationBehaviour`,
        delay: 300,
        callback: async () => {
          try {
            const formEvoNotificationBehaviour = await ospreyAPI({
              method: 'GET',
              service: 'lookups',
              endpoint: '/OspreyConfiguration/GetFormEvoNotificationBehaviour'
            })

            resolve(formEvoNotificationBehaviour)
          } catch (err) {
            console.error('Error fetching formEvoNotificationBehaviour:', err)
            reject(err)
          }
        }
      })
    })
  },
  fetchFormEvoNotificationBehaviourStatus: async () => {
    return new Promise((resolve, reject) => {
      debounce({
        id: `fetchFormEvoNotificationBehaviourStatus`,
        delay: 300,
        callback: async () => {
          try {
            const formEvoNotificationBehaviourStatus = await ospreyAPI({
              method: 'GET',
              service: 'lookups',
              endpoint: '/OspreyConfiguration/GetFormEvoNotificationBehaviourStatus'
            })

            resolve(formEvoNotificationBehaviourStatus)
          } catch (err) {
            console.error('Error fetching formEvoNotificationBehaviourStatus:', err)
            reject(err)
          }
        }
      })
    })
  },
  fetchFormEvoFormUpdateBehaviour: async () => {
    return new Promise((resolve, reject) => {
      debounce({
        id: `fetchFormEvoFormUpdateBehaviour`,
        delay: 300,
        callback: async () => {
          try {
            const formEvoFormUpdateBehaviour = await ospreyAPI({
              method: 'GET',
              service: 'lookups',
              endpoint: '/OspreyConfiguration/GetFormEvoFormUpdateBehaviour'
            })

            resolve(formEvoFormUpdateBehaviour)
          } catch (err) {
            console.error('Error fetching formEvoFormUpdateBehaviour:', err)
            reject(err)
          }
        }
      })
    })
  },
  setFormEvoNotificationBehaviour: async ({ formEvoNotificationBehaviour }) => {
    return new Promise((resolve, reject) => {
      debounce({
        id: `setFormEvoNotificationBehaviour`,
        delay: 300,
        callback: async () => {
          try {
            const response = await ospreyAPI({
              method: 'PUT',
              service: 'lookups',
              endpoint: '/OspreyConfiguration/SetFormEvoNotificationBehaviour',
              data: {
                formEvoNotificationBehaviour: formEvoNotificationBehaviour
              },
              sendAsUrlParams: true
            })

            resolve(response)
          } catch (err) {
            console.error('Error setting formEvoNotificationBehaviour:', err)
            reject(err)
          }
        }
      })
    })
  },
  setFormEvoNotificationBehaviourStatus: async ({ formEvoNotificationBehaviourStatus }) => {
    return new Promise((resolve, reject) => {
      debounce({
        id: `setFormEvoNotificationBehaviourStatus`,
        delay: 300,
        callback: async () => {
          try {
            const response = await ospreyAPI({
              method: 'PUT',
              service: 'lookups',
              endpoint: '/OspreyConfiguration/SetFormEvoNotificationBehaviourStatus',
              data: {
                formEvoNotificationBehaviourStatus: formEvoNotificationBehaviourStatus
              },
              sendAsUrlParams: true
            })

            resolve(response)
          } catch (err) {
            console.error('Error setting formEvoNotificationBehaviourStatus:', err)
            reject(err)
          }
        }
      })
    })
  },
  setFormEvoFormUpdateBehaviour: async ({ formEvoFormUpdateBehaviour }) => {
    return new Promise((resolve, reject) => {
      debounce({
        id: `setFormEvoFormUpdateBehaviour`,
        delay: 300,
        callback: async () => {
          try {
            const response = await ospreyAPI({
              method: 'PUT',
              service: 'lookups',
              endpoint: '/OspreyConfiguration/SetFormEvoFormUpdateBehaviour',
              data: {
                formEvoFormUpdateBehaviour: formEvoFormUpdateBehaviour
              },
              sendAsUrlParams: true
            })

            resolve(response)
          } catch (err) {
            console.error('Error setting formEvoFormUpdateBehaviour:', err)
            reject(err)
          }
        }
      })
    })
  },
  fetchFormEvoMatterHistoryUpdateBehavior: async () => {
    return new Promise((resolve, reject) => {
      debounce({
        id: `fetchFormEvoMatterHistoryUpdateBehavior`,
        delay: 300,
        callback: async () => {
          try {
            const response = await ospreyAPI({
              method: 'GET',
              service: 'lookups',
              endpoint: '/OspreyConfiguration/GetFormEvoMatterHistoryUpdateBehaviour'
            })

            resolve(response)
          } catch (err) {
            console.error('Error fetching formEvoMatterHistoryUpdateBehaviour:', err)
            reject(err)
          }
        }
      })
    })
  },
  setFormEvoMatterHistoryUpdateBehavior: async ({ formEvoMatterHistoryUpdateBehavior }) => {
    return new Promise((resolve, reject) => {
      debounce({
        id: `setFormEvoMatterHistoryUpdateBehavior`,
        delay: 300,
        callback: async () => {
          try {
            const response = await ospreyAPI({
              method: 'PUT',
              service: 'lookups',
              endpoint: '/OspreyConfiguration/SetFormEvoMatterHistoryUpdateBehavior',
              data: {
                formEvoMatterHistoryUpdateBehavior: formEvoMatterHistoryUpdateBehavior
              },
              sendAsUrlParams: true
            })

            resolve(response)
          } catch (err) {
            console.error('Error setting formEvoMatterHistoryUpdateBehavior:', err)
            reject(err)
          }
        }
      })
    })
  },
  resetBranch: () => set({ branch: [] }),
  resetDepartment: () => set({ department: [] }),
  resetRemunerationType: () => set({ remunerationType: null }),
  resetWorkflowType: () => set({ workflowType: null }),
  reset: () => set(initialState)
}))
